import React, { FC, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import {
  isSaturday, nextSaturday, addDays, subDays,
} from 'date-fns';
import type { Price } from 'src/common/types/api/v4/search/v1/landing';
import type { PageDataFromBreadCrumbs } from 'src/search/utils/getPageDataFromBreadcrumbs';
import type { CategorySlug } from 'src/common/boatCategories';
import { DEFAULT_SLUG } from 'src/search/constants';
import { PATHS } from 'src/common/constants';
import { formatDate, todayDate } from 'src/common/utils/dateHelpers';
import Typography from 'src/common/components/Typography';
import Button from 'src/common/components/ButtonDS22';
import { useCurrencyContext } from 'src/common/providers/CurrencyProvider';
import DlpLayout from 'src/search/components/DlpLayout';
import { useSearchInputContext } from 'src/common/components/Search/SearchInputProvider';
import nonCaptainableCategories from './nonCaptainableCategories';
import styles from './DlpFilters.module.scss';

export type DlpFiltersProps = {
  primarySlug: CategorySlug | typeof DEFAULT_SLUG;
} & Pick<Price, 'price'>
& Pick<PageDataFromBreadCrumbs, 'locationSlug'>;

/**
 * Find date of next upcoming Saturday(including 'today')
 * @returns Array of target Saturday, previous, and next dates, formatted in 'YYYY-MM-DD'
 */
const getWeekendDates = () => {
  const isTodaySaturday = isSaturday(todayDate());
  const nextTargetDate = isTodaySaturday ? todayDate() : nextSaturday(todayDate());
  const previousDate = subDays(nextTargetDate, 1);
  const nextDate = addDays(nextTargetDate, 1);

  const searchDates = [
    formatDate(nextTargetDate),
    formatDate(nextDate),
  ];
  if (!isTodaySaturday) {
    searchDates.push(formatDate(previousDate));
  }

  return searchDates;
};

type FilterParams = {
  dates?: string[];
  min_price?: number;
  max_price?: number;
  capacity?: number;
  bareboat?: boolean;
  captained?: boolean;
};

const DlpFilters: FC<DlpFiltersProps> = ({
  price,
  locationSlug,
  primarySlug,
}) => {
  const [t] = useTranslation('discovery-landing-pages');
  const { currency: { symbol } } = useCurrencyContext();
  const { filterDates } = useSearchInputContext();

  const filterChips = useMemo(() => {
    const searchDates = filterDates?.map((date) => formatDate(date));

    const pathWithParams = (params: FilterParams) => {
      const searchParams = new URLSearchParams();
      searchParams.append('page', '1');
      Object.keys(params).map((key) => {
        if (Array.isArray(params[key])) {
          return params[key].map((item: string) => searchParams.append(key, item));
        }
        return searchParams.append(key, params[key]);
      });
      const categorySegment = primarySlug === DEFAULT_SLUG ? '' : `${primarySlug}/`;
      const locationSegment = locationSlug ? `${locationSlug}/` : '';
      const basePath = `${PATHS.SEARCH}${categorySegment}${locationSegment}`;
      return `${basePath}?${searchParams.toString()}`;
    };

    const filters = [
      {
        label: t('suggested-searches--chip-3'),
        href: pathWithParams({
          dates: getWeekendDates(),
        }),
      },
    ];

    if (price?.min != null && price?.max != null) {
      filters.push({
        label: t('suggested-searches--chip-1', {
          priceRangeFormatted: `${symbol}${price?.min}-${symbol}${price?.max}`,
        }),
        href: pathWithParams({
          min_price: price?.min,
          max_price: price?.max,
          dates: searchDates,
        }),
      });
    }
    filters.push({
      label: t('suggested-searches--chip-2'),
      href: pathWithParams({
        capacity: 2,
        dates: searchDates,
      }),
    });

    if (primarySlug !== DEFAULT_SLUG && nonCaptainableCategories.includes(primarySlug)) {
      return filters;
    }

    filters.push({
      label: t('suggested-searches--chip-4'),
      href: pathWithParams({
        captained: true,
        bareboat: false,
        dates: searchDates,
      }),
    });

    return filters;
  }, [t, symbol, price, primarySlug, locationSlug, filterDates]);

  return (
    <DlpLayout>
      <div className={styles.root}>
        <Typography
          size="heading-4"
          weight="semibold"
          type="h3"
        >
          {t('filters--title')}
        </Typography>
        <div className={styles.grid}>
          {filterChips.map((chip) => (
            <Button
              key={chip.label}
              type="link"
              href={chip.href}
              variant="tertiary"
              rel="nofollow"
            >
              {chip.label}
            </Button>
          ))}
        </div>
      </div>
    </DlpLayout>
  );
};

export default DlpFilters;
